/** Decides profile-gate loading and welcome redirect for first-time users. */
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  isDesktopBreakpoint,
  useBreakpoint,
} from '@/hooks/ui/useBreakpoint';
import { useTier } from '@/features/tier/useTier';
import { ONBOARDING_HINT_ONBOARDING } from '@/constants/onboardingHints';
import { isOnboardingWelcomePath } from '@/constants/onboardingRoutes';

/**
 * @returns {{
 *   profileGateActive: boolean;
 *   shouldRedirectToWelcome: boolean;
 * }}
 */
export function useOnboardingWelcomeRedirect() {
  const { pathname } = useLocation();
  const { profileLoaded, loginCount, seenHints } = useTier();
  const breakpoint = useBreakpoint();
  const isDesktop = isDesktopBreakpoint(breakpoint);

  const isWelcomeRoute = isOnboardingWelcomePath(pathname);
  // Exclude welcome itself — otherwise welcome paths would redirect-loop.
  const isAppRoute = pathname.startsWith('/app/') && !isWelcomeRoute;

  return useMemo(() => {
    const profileGateActive =
      (isAppRoute || isWelcomeRoute) && !profileLoaded;

    const shouldRedirectToWelcome =
      profileLoaded &&
      isAppRoute &&
      loginCount === 1 &&
      !seenHints.includes(ONBOARDING_HINT_ONBOARDING) &&
      isDesktop;

    return { profileGateActive, shouldRedirectToWelcome };
  }, [
    isAppRoute,
    isWelcomeRoute,
    profileLoaded,
    loginCount,
    seenHints,
    isDesktop,
  ]);
}
